<template>
  <div class="card" v-show="currentCardId == 'job-summary'">
    <div class="overflow-scroll-y">
      <div class="card-header">
        <h2>職務要約</h2>
        <p>自分の職務経歴を100～150文字程度でわかりやすくまとめましょう</p>
        <div class="add-item">
          <el-button
            v-if="inAppPayments"
            id="prime-reasonai-btn"
            type="danger"
            @click="openAiSummaryDialog"
            >自動で要約する</el-button
          >
          <el-button type="success" @click="exampleVisible = true"
            >例文から追加</el-button
          >
        </div>
      </div>
      <div class="card-content">
        <NewRirekishoExampleSentenceDialog
          :value="exampleVisible"
          name="summary-dialog"
          :items="summaryExamples"
          show-overwrite-warning
          @input="exampleVisible = $event"
          @select="onSelectExample"
        />
        <AiMyprDialog
          :visible="aiSummaryVisible"
          @update:visible="onSummaryVisible"
          @before-close="cancelAIStream"
        >
          <div class="v--modal-close-top">
            <el-button
              icon="el-icon-circle-close"
              type="text"
              @click="closeAiSummaryDialog"
            />
          </div>
          <div class="mypr-title-block">
            <p class="mypr-title-catch">ヤギプライム限定</p>
            <h3>AI職務要約作成</h3>
            <p class="mypr-lead">
              AIがこれまでの経歴やスキルを分析し、あなたの強みが伝わる職務要約を作成します。
            </p>
          </div>
          <div class="mypr-popup-btnarea" v-if="inAppPayments">
            <p class="count-txt" v-if="isYagiPrime">
              <span>利用回数：</span
              ><span>{{ aiUsageCount }} / {{ aiMaxUsageCount }}回</span>
            </p>
            <p class="count-txt" v-else>
              <span>利用回数：1回</span>
            </p>
            <p class="mypr-attention">
              ※AI再生成をすると、現在入力中の職務要約は<br />上書きされますのでご注意ください。
            </p>
            <div
              class="prime-cv-block"
              style="margin-top: 0"
              v-show="!aiIsStreaming"
            >
              <el-button @click="getAISummary">
                <span>{{
                  !isAuthenticated || !isYagiPrime
                    ? '🔒️ 下記の内容でAI職務要約を作成'
                    : '下記の内容でAI職務要約を作成'
                }}</span>
              </el-button>
            </div>
          </div>
          <div v-if="!aiSummaryResultView">
            <ul class="example-sentence-list">
              <li class="example-sentence-item example-sentence-item--self">
                <p class="question-lead">以下の内容を参考に要約を作成します</p>
                <p class="question-title">■ スキル</p>
                <div class="el-textarea mb10">
                  <ul class="summary-preview-list">
                    <li
                      v-for="(item, index) in formatData.skillsToUse"
                      :key="index"
                      class="reason-txt"
                    >
                      {{ item.skillname }}
                      <span v-if="item.years"
                        >/ 実務経験 {{ item.years }}年以上</span
                      >
                    </li>
                    <li
                      v-if="
                        !formatData.skillsToUse ||
                        !formatData.skillsToUse.length
                      "
                      class="empty-text reason-txt"
                    >
                      スキルが登録されていません
                    </li>
                  </ul>
                </div>
                <p class="question-title">■ 職務経歴</p>
                <div class="el-textarea reason-txt">
                  <div
                    v-for="item in formatData.jobHistories"
                    :key="item.uuid"
                    class="history-preview-item"
                  >
                    <p class="history-company-name">{{ item.companyName }}</p>
                    <p class="history-period">
                      <span v-if="item.isContinuing">在職中</span>
                      <span v-else
                        >{{ item.startYear }}年{{ item.startMonth }}月 〜
                        {{ item.endYear }}年{{ item.endMonth }}月</span
                      >
                    </p>
                    <ul class="project-preview-list">
                      <li v-for="(proj, idx) in item.projects" :key="idx">
                        {{ proj.projectTitle }}
                      </li>
                    </ul>
                  </div>
                  <p
                    v-if="
                      !formatData.jobHistories ||
                      !formatData.jobHistories.length
                    "
                    class="empty-text reason-txt"
                  >
                    経歴が登録されていません
                  </p>
                </div>
              </li>
            </ul>
          </div>
          <div v-else>
            <div
              v-if="aiIsThinking"
              class="ai-thinking"
              @click="toggleAiReasoning"
            >
              <span class="ai-thinking__icon"></span>
              <span class="ai-thinking__label">{{ aiThinkingLabel }}</span>
              <span class="ai-thinking__dots">
                <span class="ai-thinking__dot"></span>
                <span class="ai-thinking__dot"></span>
                <span class="ai-thinking__dot"></span>
              </span>
            </div>
            <div
              v-if="aiReasoningText"
              class="ai-reasoning"
              :class="
                aiShowReasoning
                  ? 'ai-reasoning--expanded'
                  : 'ai-reasoning--collapsed'
              "
            >
              <div class="ai-reasoning__content">{{ aiReasoningText }}</div>
            </div>
            <div
              v-if="!aiIsThinking && aiReasoningText"
              class="ai-reasoning__toggle"
              @click="toggleAiReasoning"
            >
              <span
                class="ai-reasoning__arrow"
                :class="{ 'ai-reasoning__arrow--open': aiShowReasoning }"
                >▶</span
              >
              {{ aiShowReasoning ? '思考過程を隠す' : '思考過程を見る' }}
            </div>
            <ul class="example-sentence">
              <li @click="applySummaryResult">
                <div class="title">AIからの提案</div>
                <div class="body">
                  <span v-html="aiResultSummaryResult"></span
                  ><span
                    v-if="aiIsStreaming && !aiIsThinking"
                    class="ai-cursor"
                  ></span>
                </div>
              </li>
            </ul>
            <div v-if="!isYagiPrime" class="text-right" v-show="!aiIsStreaming">
              <p class="register-lead">＼全文を表示する場合は／</p>
              <el-button
                id="prime-reasonai-register-btn"
                class="prime-register-btn"
                @click="goYagiprimeRegisterSummary"
                >ヤギプライム会員になる</el-button
              >
            </div>
          </div>
          <div class="text-right" v-show="!aiIsStreaming">
            <el-button
              v-if="isYagiPrime && aiSummaryResultView"
              type="primary"
              @click="aiSummaryResultView = false"
              >もう一度作る</el-button
            >
            <el-button @click="closeAiSummaryDialog">閉じる</el-button>
          </div>
        </AiMyprDialog>
        <el-input
          type="textarea"
          v-model="formatData.jobSummary"
          v-proofread
          :autosize="{ minRows: 5, maxRows: 15 }"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import NewRirekishoExampleSentenceDialog from '@/components/format-edit/new-rirekisho/NewRirekishoExampleSentenceDialog.vue'
import AiMyprDialog from '@/components/format-edit/AiMyprDialog.vue'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import {
  useShokurekishoFormatData,
  toExampleDialogItems,
  type ShokurekishoExampleSentences
} from '@/composables/format-edit/useShokurekishoFormatData'
import { useShokurekishoAiSummary } from '@/composables/format-edit/useShokurekishoAiSummary'
import '@/assets/css/ai-streaming.css'

const props = defineProps<{
  currentCardId?: string
  exampleSentences?: ShokurekishoExampleSentences
}>()

const formatData = useShokurekishoFormatData()
const { globalVar } = useHostServices()
const { isAuthenticated } = useAuth()
const exampleVisible = ref(false)

const inAppPayments = computed(
  () =>
    !!(globalVar as typeof globalVar & { in_app_payments?: boolean })
      .in_app_payments
)
const isYagiPrime = computed(() => !!globalVar.isYagiPrime)

const summaryExamples = computed(() =>
  toExampleDialogItems(
    props.exampleSentences?.summary,
    formatData.occupation || 'others'
  )
)

const {
  aiSummaryVisible,
  aiSummaryResultView,
  aiResultSummaryResult,
  aiUsageCount,
  aiMaxUsageCount,
  aiIsStreaming,
  aiIsThinking,
  aiThinkingLabel,
  aiReasoningText,
  aiShowReasoning,
  openAiSummaryDialog,
  closeAiSummaryDialog,
  getAISummary,
  goYagiprimeRegisterSummary,
  applySummaryResult,
  toggleAiReasoning,
  cancelAIStream
} = useShokurekishoAiSummary(formatData)

function onSelectExample(item: { description: string }) {
  formatData.jobSummary = item.description.replace(/<br>/gi, '\n')
}

function onSummaryVisible(value: boolean) {
  aiSummaryVisible.value = value
  if (!value) cancelAIStream()
}
</script>

<style lang="scss" scoped>
.el-button--danger {
  position: relative;
  background-color: #ff8c00;
  border-color: #ff8c00;

  &::after {
    position: absolute;
    content: '✨';
    font-size: 20px;
    top: -5px;
    right: -7px;
  }
}

.mb10 {
  margin-bottom: 10px;
}
</style>
